import React from 'react';
import { DocsLayout } from '../../components/docs/DocsLayout';
import { CodeBlock } from '../../components/docs/CodeBlock';
import { Callout } from '../../components/docs/Callout';
import { RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
export function IdempotencyGuidePage() {
  const idempotencyCode = {
    curl: `curl -X POST https://api.obseq.com/v1/payments/payouts \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: 550e8400-e29b-41d4-a716-446655440000" \\
  -d '{
    "amount": 250000,
    "currency": "USD",
    "beneficiary_id": "bn_123"
  }'`,
    node: `import { randomUUID } from 'crypto';

const idempotencyKey = randomUUID();

const response = await fetch('https://api.obseq.com/v1/payments/payouts', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json',
    'Idempotency-Key': idempotencyKey
  },
  body: JSON.stringify({
    amount: 250000,
    currency: 'USD',
    beneficiary_id: 'bn_123'
  })
});`,
    python: `import uuid
import requests

idempotency_key = str(uuid.uuid4())

response = requests.post(
    'https://api.obseq.com/v1/payments/payouts',
    headers={
        'Authorization': f'Bearer {api_key}',
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotency_key
    },
    json={
        'amount': 250000,
        'currency': 'USD',
        'beneficiary_id': 'bn_123'
    }
)`,
    go: `import "github.com/google/uuid"

idempotencyKey := uuid.New().String()

req, _ := http.NewRequest("POST", "https://api.obseq.com/v1/payments/payouts", body)
req.Header.Set("Authorization", "Bearer "+apiKey)
req.Header.Set("Content-Type", "application/json")
req.Header.Set("Idempotency-Key", idempotencyKey)

client := &http.Client{}
resp, _ := client.Do(req)`,
    java: `import java.util.UUID;

String idempotencyKey = UUID.randomUUID().toString();

HttpRequest request = HttpRequest.newBuilder()
    .uri(URI.create("https://api.obseq.com/v1/payments/payouts"))
    .header("Authorization", "Bearer " + apiKey)
    .header("Content-Type", "application/json")
    .header("Idempotency-Key", idempotencyKey)
    .POST(HttpRequest.BodyPublishers.ofString(jsonBody))
    .build();`,
    php: `<?php
use Ramsey\\Uuid\\Uuid;

$idempotencyKey = Uuid::uuid4()->toString();

$response = $client->post('https://api.obseq.com/v1/payments/payouts', [
    'headers' => [
        'Authorization' => 'Bearer ' . $apiKey,
        'Content-Type' => 'application/json',
        'Idempotency-Key' => $idempotencyKey
    ],
    'json' => [
        'amount' => 250000,
        'currency' => 'USD',
        'beneficiary_id' => 'bn_123'
    ]
]);`,
    ruby: `require 'securerandom'

idempotency_key = SecureRandom.uuid

response = HTTParty.post(
  'https://api.obseq.com/v1/payments/payouts',
  headers: {
    'Authorization' => "Bearer #{api_key}",
    'Content-Type' => 'application/json',
    'Idempotency-Key' => idempotency_key
  },
  body: {
    amount: 250000,
    currency: 'USD',
    beneficiary_id: 'bn_123'
  }.to_json
)`,
    dotnet: `using System;

var idempotencyKey = Guid.NewGuid().ToString();

var request = new HttpRequestMessage(HttpMethod.Post, 
    "https://api.obseq.com/v1/payments/payouts");
request.Headers.Add("Authorization", $"Bearer {apiKey}");
request.Headers.Add("Idempotency-Key", idempotencyKey);
request.Content = new StringContent(jsonBody, 
    Encoding.UTF8, "application/json");

var response = await client.SendAsync(request);`
  };
  return <DocsLayout breadcrumbs={[{
    label: 'Docs',
    href: '/docs'
  }, {
    label: 'Guides',
    href: '/docs/guides'
  }, {
    label: 'Idempotency',
    href: '/docs/guides/idempotency'
  }]} onThisPage={[{
    title: 'Overview',
    id: 'overview'
  }, {
    title: 'When to Use',
    id: 'when-to-use'
  }, {
    title: 'How It Works',
    id: 'how-it-works'
  }, {
    title: 'Conflict Behavior',
    id: 'conflicts'
  }, {
    title: 'Retry Guidance',
    id: 'retries'
  }, {
    title: 'Code Examples',
    id: 'examples'
  }]} data-id="element-1495">
      <h1 data-id="element-1496">Idempotency</h1>
      <p className="lead" data-id="element-1497">
        Idempotency ensures that retrying the same request produces the same
        result, preventing duplicate operations like double charges or duplicate
        payouts.
      </p>

      <h2 id="overview" data-id="element-1498">Overview</h2>
      <p data-id="element-1499">
        Network failures and timeouts are inevitable. Idempotency keys allow you
        to safely retry requests without worrying about duplicate operations.
      </p>

      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 my-6" data-id="element-1500">
        <div className="flex items-start gap-3" data-id="element-1501">
          <RefreshCw className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" data-id="element-1502" />
          <div data-id="element-1503">
            <h3 className="text-lg font-semibold text-blue-900 mb-2" data-id="element-1504">
              Key Concept
            </h3>
            <p className="text-sm text-blue-800" data-id="element-1505">
              Same idempotency key + same route + same request body = same
              result, even if called multiple times.
            </p>
          </div>
        </div>
      </div>

      <h2 id="when-to-use" data-id="element-1506">When to Use Idempotency Keys</h2>
      <p data-id="element-1507">
        Include an <code data-id="element-1508">Idempotency-Key</code> header for all unsafe
        operations:
      </p>
      <ul data-id="element-1509">
        <li data-id="element-1510">
          <strong data-id="element-1511">POST requests</strong> - Creating resources (payouts,
          transfers, etc.)
        </li>
        <li data-id="element-1512">
          <strong data-id="element-1513">PUT requests</strong> - Updating resources
        </li>
      </ul>

      <Callout type="tip" data-id="element-1514">
        GET and DELETE requests are naturally idempotent and don't require
        idempotency keys.
      </Callout>

      <h3 data-id="element-1515">Use Cases</h3>
      <table className="w-full text-sm my-4" data-id="element-1516">
        <thead data-id="element-1517">
          <tr className="border-b border-slate-200" data-id="element-1518">
            <th className="text-left py-2 pr-4" data-id="element-1519">Scenario</th>
            <th className="text-left py-2" data-id="element-1520">Why It Matters</th>
          </tr>
        </thead>
        <tbody data-id="element-1521">
          <tr className="border-b border-slate-100" data-id="element-1522">
            <td className="py-3 pr-4" data-id="element-1523">Network timeout</td>
            <td className="py-3 text-slate-600" data-id="element-1524">
              Request may have succeeded but response was lost
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1525">
            <td className="py-3 pr-4" data-id="element-1526">User retry</td>
            <td className="py-3 text-slate-600" data-id="element-1527">
              User clicks "Submit" multiple times
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1528">
            <td className="py-3 pr-4" data-id="element-1529">Webhook replay</td>
            <td className="py-3 text-slate-600" data-id="element-1530">
              Processing the same event multiple times
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1531">
            <td className="py-3 pr-4" data-id="element-1532">Background job retry</td>
            <td className="py-3 text-slate-600" data-id="element-1533">
              Job queue retries failed tasks
            </td>
          </tr>
        </tbody>
      </table>

      <h2 id="how-it-works" data-id="element-1534">How It Works</h2>
      <p data-id="element-1535">
        Obseq stores the idempotency key and request details when processing a
        request:
      </p>

      <ol data-id="element-1536">
        <li data-id="element-1537">First request with a new idempotency key is processed normally</li>
        <li data-id="element-1538">Obseq stores the key, request body, and response</li>
        <li data-id="element-1539">
          Subsequent requests with the same key return the cached response
        </li>
        <li data-id="element-1540">Keys expire after 24 hours</li>
      </ol>

      <Callout type="note" data-id="element-1541">
        Use UUIDs (v4) for idempotency keys. They're unique, random, and widely
        supported.
      </Callout>

      <h2 id="conflicts" data-id="element-1542">Conflict Behavior</h2>
      <p data-id="element-1543">
        If you reuse an idempotency key with{' '}
        <strong data-id="element-1544">different request parameters</strong>, Obseq returns a 409
        Conflict error:
      </p>

      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4" data-id="element-1545">
        <code data-id="element-1546">{`{
  "error": {
    "type": "conflict_error",
    "code": "duplicate_request",
    "message": "Idempotency key already used with different parameters.",
    "request_id": "req_abc123"
  }
}`}</code>
      </pre>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6" data-id="element-1547">
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4" data-id="element-1548">
          <div className="flex items-center gap-2 mb-2" data-id="element-1549">
            <CheckCircle className="w-5 h-5 text-green-600" data-id="element-1550" />
            <h4 className="font-semibold text-green-900" data-id="element-1551">Safe Retry</h4>
          </div>
          <p className="text-sm text-green-800" data-id="element-1552">
            Same key + same parameters = cached response returned
          </p>
        </div>

        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4" data-id="element-1553">
          <div className="flex items-center gap-2 mb-2" data-id="element-1554">
            <AlertCircle className="w-5 h-5 text-red-600" data-id="element-1555" />
            <h4 className="font-semibold text-red-900" data-id="element-1556">Conflict</h4>
          </div>
          <p className="text-sm text-red-800" data-id="element-1557">
            Same key + different parameters = 409 error
          </p>
        </div>
      </div>

      <h2 id="retries" data-id="element-1558">Retry Guidance</h2>
      <p data-id="element-1559">Follow these guidelines when retrying requests:</p>

      <h3 data-id="element-1560">Network Errors (Timeouts, Connection Failures)</h3>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-4" data-id="element-1561">
        <p className="text-sm text-slate-700 mb-2" data-id="element-1562">
          <strong data-id="element-1563">✓ Safe to retry</strong> with the same idempotency key
        </p>
        <ul className="text-sm text-slate-600 space-y-1" data-id="element-1564">
          <li data-id="element-1565">• Use exponential backoff (1s, 2s, 4s, 8s)</li>
          <li data-id="element-1566">• Retry up to 3-5 times</li>
          <li data-id="element-1567">• Keep the same idempotency key</li>
        </ul>
      </div>

      <h3 data-id="element-1568">4xx Client Errors (400, 401, 403, 404)</h3>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-4" data-id="element-1569">
        <p className="text-sm text-slate-700 mb-2" data-id="element-1570">
          <strong data-id="element-1571">✗ Don't retry</strong> without fixing the issue
        </p>
        <ul className="text-sm text-slate-600 space-y-1" data-id="element-1572">
          <li data-id="element-1573">• Fix the request parameters</li>
          <li data-id="element-1574">• Use a new idempotency key if you change parameters</li>
          <li data-id="element-1575">• Log the error for investigation</li>
        </ul>
      </div>

      <h3 data-id="element-1576">5xx Server Errors (500, 502, 503)</h3>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-4" data-id="element-1577">
        <p className="text-sm text-slate-700 mb-2" data-id="element-1578">
          <strong data-id="element-1579">✓ Safe to retry</strong> with the same idempotency key
        </p>
        <ul className="text-sm text-slate-600 space-y-1" data-id="element-1580">
          <li data-id="element-1581">• Use exponential backoff</li>
          <li data-id="element-1582">• Retry up to 3 times</li>
          <li data-id="element-1583">• Contact support if errors persist</li>
        </ul>
      </div>

      <h3 data-id="element-1584">409 Conflict Errors</h3>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-4" data-id="element-1585">
        <p className="text-sm text-slate-700 mb-2" data-id="element-1586">
          <strong data-id="element-1587">⚠ Generate a new key</strong> if parameters changed
        </p>
        <ul className="text-sm text-slate-600 space-y-1" data-id="element-1588">
          <li data-id="element-1589">• If parameters are the same, this indicates a bug</li>
          <li data-id="element-1590">• If parameters changed, use a new idempotency key</li>
          <li data-id="element-1591">• Check your key generation logic</li>
        </ul>
      </div>

      <h2 id="examples" data-id="element-1592">Code Examples</h2>
      <p data-id="element-1593">Generate and use idempotency keys in your preferred language:</p>
      <CodeBlock code={idempotencyCode} data-id="element-1594" />

      <h3 data-id="element-1595">Best Practices</h3>
      <ul data-id="element-1596">
        <li data-id="element-1597">Generate a new UUID for each unique operation</li>
        <li data-id="element-1598">Store idempotency keys with your request metadata</li>
        <li data-id="element-1599">Don't reuse keys across different operations</li>
        <li data-id="element-1600">Use the same key when retrying the exact same request</li>
        <li data-id="element-1601">Include idempotency keys in logs for debugging</li>
      </ul>

      <Callout type="warning" data-id="element-1602">
        Never use predictable values (timestamps, sequential IDs) as idempotency
        keys. Always use random UUIDs.
      </Callout>

      <h3 data-id="element-1603">Example: Retry Logic</h3>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4" data-id="element-1604">
        <code data-id="element-1605">{`async function createPayoutWithRetry(payoutData, maxRetries = 3) {
  const idempotencyKey = crypto.randomUUID();
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch('https://api.obseq.com/v1/payments/payouts', {
        method: 'POST',
        headers: {
          'Authorization': \`Bearer \${apiKey}\`,
          'Idempotency-Key': idempotencyKey
        },
        body: JSON.stringify(payoutData)
      });
      
      if (response.ok) {
        return await response.json();
      }
      
      // Don't retry 4xx errors (except 429)
      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        throw new Error(\`Client error: \${response.status}\`);
      }
      
      lastError = new Error(\`HTTP \${response.status}\`);
    } catch (error) {
      lastError = error;
    }
    
    // Exponential backoff
    if (attempt < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    }
  }
  
  throw lastError;
}`}</code>
      </pre>

      <Callout type="note" data-id="element-1606">
        For complete idempotency specifications, see{' '}
        <a href="#/docs/api-conventions" className="text-blue-600 hover:text-blue-700" data-id="element-1607">
          API Conventions
        </a>
        .
      </Callout>

      <div className="mt-12 pt-8 border-t border-slate-200" data-id="element-1608">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-1609">
          Related Guides
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1610">
          <a href="#/docs/guides/errors" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1611">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1612">
              Error Handling
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1613">
              Handle API errors gracefully
            </p>
          </a>
          <a href="#/docs/api-conventions" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1614">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1615">
              API Conventions
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1616">
              Complete API specifications
            </p>
          </a>
        </div>
      </div>
    </DocsLayout>;
}